/* eslint-disable react-refresh/only-export-components */
import React, { useContext, useState, useCallback } from 'react';

type PreviewContent = React.ReactNode | string | string[];

interface PreviewOptions {
  fullscreen?: boolean;
  content: PreviewContent;
  title?: string;
  description?: React.ReactNode;
}

interface PreviewContextType {
  preview: (_options: PreviewOptions) => void;
  close: () => void;
  isOpen: boolean;
  content: PreviewContent | null;
  title?: string;
  fullscreen: boolean;
  description?: React.ReactNode;
}

const Context = React.createContext<PreviewContextType>({
  preview: () => {},
  close: () => {},
  isOpen: false,
  content: null,
  title: undefined,
  fullscreen: false,
  description: undefined,
});

const PreviewProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [content, setContent] = useState<PreviewContent | null>(null);
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState<React.ReactNode | undefined>(undefined);

  const preview = useCallback((options: PreviewOptions) => {
    setContent(options.content);
    setTitle(options.title);
    setDescription(options.description);
    setFullscreen(!!options.fullscreen);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // Clear content after animation completes
    setTimeout(() => {
      setContent(null);
      setFullscreen(false);
      setTitle(undefined);
      setDescription(undefined);
    }, 300);
  }, []);

  const contextValue: PreviewContextType = {
    preview,
    close,
    isOpen,
    content,
    title,
    fullscreen,
    description,
  };

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export const usePreview = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error('usePreview must be used within a PreviewProvider');
  }
  return context.preview;
};

export const usePreviewContext = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error('usePreviewContext must be used within a PreviewProvider');
  }
  return context;
};

export default PreviewProvider;
